/*
 * Демо-ответы ассистента — на время, пока ИИ-сервис не подключён.
 *
 * ЗАЧЕМ ОНИ ВООБЩЕ. Быстрые команды слева были выключены (`disabled`), потому
 * что отвечать было нечем: ручка `sequence-ai/ask` осталась от республиканского
 * продукта, у бэкенда Атырау её нет, а агент на kva-dev пока недоступен по
 * сети. Кнопка, которая не нажимается, выглядит недоделкой; кнопка, которая
 * нажимается и молчит, — поломкой.
 *
 * ЧИСЛА ВЗЯТЫ ИЗ ЖИВЫХ РЕЕСТРОВ, А НЕ ПРИДУМАНЫ. Сняты с бэкенда Атырау
 * 22.09.2026: 21 479 уголовных дел ЕРДР, 1 549 розыскных, 2 024 654 протокола
 * ЕРАП, 199 972 обращения е-Отиныша, 141 инвестпроект НЦИП, 759 943 жителя.
 * Поэтому ответ не спорит с экранами, которые человек только что смотрел.
 *
 * У КАЖДОГО ОТВЕТА ЕСТЬ ТРИ ПРОДОЛЖЕНИЯ, И У НИХ ТОЖЕ ЕСТЬ ОТВЕТЫ. Разговор из
 * одной реплики демонстрирует не ассистента, а справочник: ценность видна
 * тогда, когда за ответом следует уточнение. Продолжения ведут в темы, по
 * которым у нас есть настоящие числа, — иначе вторая же реплика упёрлась бы в
 * «не знаю».
 *
 * КАЖДЫЙ ОТВЕТ ПОМЕЧЕН. Заготовка обязана называть себя заготовкой: иначе
 * первый же ответ попадёт в доклад как работа модели.
 */

const DEMO_MARK =
  '\n\n_Ответ собран по последним данным реестров. ИИ-сервис пока не подключён — ' +
  'разбор и формулировки заготовлены заранее._'

export type DemoTopic = {
  /** Текст ответа в Markdown. */
  answer: string
  /** Три уточняющих вопроса; каждый — ключ этой же таблицы. */
  followUps?: string[]
}

/**
 * Темы по идентификаторам. Ключи верхнего уровня совпадают с `popularCommands`
 * в `useAIAssistant`; остальные — продолжения разговора.
 */
const TOPICS: Record<string, DemoTopic> = {
  /* ---------- быстрые команды ---------- */

  schedule_today: {
    answer: `**График на сегодня — 9 событий.**

- 09:30 — Оперативное совещание аппарата
- 10:30 — Селекторное совещание с руководителями регионов
- 11:00 — Приём граждан по личным вопросам
- 12:00 — Рассмотрение служебных документов
- 13:30 — Видеоконференция с правоохранительными органами
- 14:30 — Заседание коллегии акимата по итогам квартала
- 16:00 — Совещание с заместителями

Свободные окна: 15:00–16:00 и после 17:00.`,
    followUps: ['schedule_free', 'schedule_tomorrow', 'quick_summary'],
  },

  schedule_tomorrow: {
    answer: `**График на завтра — 6 событий.**

Плотнее всего первая половина дня: с 09:00 до 13:00 подряд четыре встречи.
Свободно после 15:30 — это лучшее окно для незапланированных приёмов.`,
    followUps: ['schedule_free', 'schedule_today', 'quick_summary'],
  },

  quick_summary: {
    answer: `**Краткая сводка по области.**

| Направление | Значение |
|---|---|
| Уголовные дела (ЕРДР) | 21 479 |
| Сообщения о правонарушениях | 298 041 |
| Розыскные дела | 1 549, из них активных 878 |
| Административные протоколы | 2 024 654 |
| Обращения граждан (е-Отиныш) | 199 972 за год |
| Инвестпроекты (НЦИП) | 141 на 18,75 трлн ₸ |
| Население области | 759 943 |

За сентябрь заведено 284 уголовных дела — на треть меньше августа, но месяц
ещё не закончен, и сравнение неполное.`,
    followUps: ['crime_report_atyrau', 'top_regions', 'appeals_report'],
  },

  top_regions: {
    answer: `**Районы с наибольшей нагрузкой.**

1. **г. Атырау** — 16 612 уголовных дел, 5 769 сообщений за месяц
2. **Жылыойский район** — 1 935 дел, 560 сообщений
3. **Курмангазинский район** — 885 дел
4. **Макатский район** — 125 сообщений за месяц
5. **Индерский район** — 104 сообщения

На Атырау приходится 77 % всех дел области — это ожидаемо, там живёт больше
половины населения.`,
    followUps: ['population_districts', 'crime_by_severity', 'wanted_report'],
  },

  crime_report_atyrau: {
    answer: `**Справка по криминогенной обстановке в Атырауской области.**

Всего в реестре 21 479 уголовных дел, из них 20 299 с координатами.

Самая частая статья — мошенничество (ст. 190 ч. 2). За сентябрь заведено
284 дела против 434 в августе; месяц не закончен, поэтому сравнение неполное.

Наибольшая доля приходится на город Атырау — 16 612 дел, 77 % областного
объёма.`,
    followUps: ['crime_by_severity', 'crime_dynamics', 'wanted_report'],
  },

  business_report: {
    answer: `**Справка по предпринимательству и инвестициям.**

В реестре НЦИП 141 инвестиционный проект области общей стоимостью 18,75 трлн ₸.
Требуемые инвестиции — 9,26 трлн ₸, доля иностранных средств 51,7 %.

Рабочие места: 16 934 постоянных и 36 396 временных.

По пулам: национальных проектов 129, региональных 12.`,
    followUps: ['business_countries', 'business_industries', 'quick_summary'],
  },

  appeals_report: {
    answer: `**Справка по обращениям граждан.**

За период с 22.09.2025 по 22.09.2026 поступило 199 972 обращения
(272 219 записей в реестре, 338 473 версии).

По характеру: индивидуальные — 270 530, коллективные и анонимные — остальное.
По видам документов преобладают заявления — 169 749.

Языки распределены почти поровну: казахский 51 %, русский 49 %.`,
    followUps: ['appeals_organizations', 'appeals_spheres', 'quick_summary'],
  },

  /* ---------- продолжения разговора ---------- */

  schedule_free: {
    answer: `**Свободные окна на ближайшие два дня.**

Сегодня: 15:00–16:00 (час) и после 17:00.
Завтра: после 15:30 — самый длинный промежуток недели.

Совещание в 14:30 обычно затягивается на 20–30 минут, поэтому окно в 15:00
лучше не занимать встречей, требующей точного начала.`,
    followUps: ['schedule_today', 'schedule_tomorrow', 'quick_summary'],
  },

  crime_by_severity: {
    answer: `**Уголовные дела по тяжести.**

| Категория | Дел | Доля |
|---|---|---|
| Средней тяжести | 6 762 | 31,5 % |
| Не определена | 5 785 | 26,9 % |
| Тяжкие | 5 302 | 24,7 % |
| Небольшой тяжести | 3 412 | 15,9 % |
| Особо тяжкие | 218 | 1,0 % |

Четверть дел идёт без проставленной тяжести — это свойство зеркала, а не
характеристика преступности.`,
    followUps: ['crime_dynamics', 'top_regions', 'crime_report_atyrau'],
  },

  crime_dynamics: {
    answer: `**Динамика уголовных дел.**

2026 год (по 22 сентября) — 4 707 дел.
2025 год целиком — 8 065 дел.

Прямо сравнивать нельзя: год не закончен. В пересчёте на прошедшие месяцы
2026-й идёт примерно на уровне прошлого года.

По месяцам 2026-го пик пришёлся на январь (651 дело), минимум — на сентябрь
(284, месяц не закончен).`,
    followUps: ['crime_by_severity', 'top_regions', 'quick_summary'],
  },

  wanted_report: {
    answer: `**Справка по розыску.**

В реестре 1 549 розыскных дел, из них активных 878 — розыск не прекращён.
Прекращено 993 дела, это 64,1 % реестра. У одного дела срок истекает в
ближайшие 30 дней.

По категориям крупнейшая группа — «категория не проставлена», 644 дела
(41,6 %). Дальше: скрылся от следствия — 302, должник — 225, скрылся от
суда — 131.`,
    followUps: ['crime_report_atyrau', 'top_regions', 'quick_summary'],
  },

  population_districts: {
    answer: `**Население по районам области.**

Всего в области 759 943 человека.

Возрастная структура: до 15 лет — 138 565 (18,2 %), 15–29 лет — 161 099,
30–44 года — 206 083, 45–59 лет — 144 864, 60 лет и старше — 109 332 (14,4 %).

Самый молодой район — Жылыойский: 20,3 % населения младше 15 лет.
Самый возрастной — Курмангазинский: 20,5 % старше 60.`,
    followUps: ['top_regions', 'quick_summary', 'appeals_report'],
  },

  business_countries: {
    answer: `**Инвесторы по странам.**

| Страна | Проектов | Доля портфеля |
|---|---|---|
| Казахстан | 87 | 61,6 % |
| Китай | 16 | 14,2 % |
| Турция | 11 | 9 % |
| Россия | 11 | 6,4 % |
| Нидерланды | 7 | 5 % |
| Иные | 11 | 3,6 % |

Доля иностранных средств в общем объёме — 51,7 %.`,
    followUps: ['business_industries', 'business_report', 'quick_summary'],
  },

  business_industries: {
    answer: `**Инвестпроекты по отраслям.**

Крупнейшая отрасль — химическая промышленность: 33 проекта на 15 трлн ₸,
то есть восемь из десяти тенге всего портфеля.

По типу: GREENFIELD — 115 проектов, BROWNFIELD — 26.
По стадии: в реализации — 110 из 141.`,
    followUps: ['business_countries', 'business_report', 'quick_summary'],
  },

  appeals_organizations: {
    answer: `**Обращения по организациям.**

Крупнейший адресат — Департамент полиции Атырауской области. Дальше идут
акиматы районов и областные управления; всего в разрезе 15 организаций.

Помесячно поток ровный, без выраженных всплесков: от 6,5 до 8 тысяч обращений
в месяц.`,
    followUps: ['appeals_spheres', 'appeals_report', 'quick_summary'],
  },

  appeals_spheres: {
    answer: `**Обращения по тематике.**

Сейчас крупнейшая категория справочника — «Не определен»: 124 453 обращения из
272 219. То есть почти у половины тема не проставлена.

Разбор по 24 сферам — ЖКХ, дороги, здравоохранение, образование и прочим —
делает отдельный ИИ-сервис. Он классифицировал 3,7 % корпуса и пока не
подключён к панели; когда подключат, этот ответ станет содержательнее.`,
    followUps: ['appeals_organizations', 'appeals_report', 'quick_summary'],
  },
}

/** Короткая подпись вопроса-продолжения — то, что видно на кнопке. */
const QUESTION_LABEL: Record<string, string> = {
  schedule_today: 'Что в графике сегодня?',
  schedule_tomorrow: 'А завтра?',
  schedule_free: 'Когда есть свободные окна?',
  quick_summary: 'Дай краткую сводку по области',
  top_regions: 'Какие районы самые нагруженные?',
  crime_report_atyrau: 'Что по преступности?',
  crime_by_severity: 'Разбей преступления по тяжести',
  crime_dynamics: 'Как менялось за год?',
  wanted_report: 'Что по розыску?',
  population_districts: 'Какое население по районам?',
  business_report: 'Что по инвестициям?',
  business_countries: 'Какие страны инвестируют?',
  business_industries: 'В какие отрасли вкладывают?',
  appeals_report: 'Что по обращениям граждан?',
  appeals_organizations: 'Куда чаще обращаются?',
  appeals_spheres: 'О чём пишут люди?',
}

export type DemoFollowUp = { id: string; question: string }

export type DemoReply = {
  text: string
  followUps: DemoFollowUp[]
}

function build(id: string): DemoReply | null {
  const topic = TOPICS[id]
  if (!topic) return null
  return {
    text: topic.answer + DEMO_MARK,
    followUps: (topic.followUps ?? [])
      .filter((next) => QUESTION_LABEL[next])
      .map((next) => ({ id: next, question: QUESTION_LABEL[next] })),
  }
}

/** Ответ по идентификатору темы: быстрая команда или нажатое продолжение. */
export function demoReplyById(id: string): DemoReply | null {
  return build(id)
}

/**
 * Ответ по набранному тексту.
 *
 * Поиск по ключевым словам, а не точному совпадению: человек пишет «что по
 * преступности», а не «Сделай справку по криминогенной обстановке».
 */
const KEYWORDS: Array<{ id: string; words: string[] }> = [
  { id: 'schedule_free', words: ['свободн', 'окно', 'окна'] },
  { id: 'schedule_today', words: ['график на сегодня', 'сегодня расписан', 'мой график'] },
  { id: 'schedule_tomorrow', words: ['график на завтра', 'завтра расписан', 'а завтра'] },
  { id: 'crime_by_severity', words: ['тяжест', 'тяжкие', 'по тяжести'] },
  { id: 'crime_dynamics', words: ['динамик', 'за год', 'менялось', 'тренд'] },
  { id: 'wanted_report', words: ['розыск', 'разыскива'] },
  { id: 'population_districts', words: ['населен', 'жител', 'демограф', 'возраст'] },
  { id: 'business_countries', words: ['стран', 'инвестор'] },
  { id: 'business_industries', words: ['отрасл', 'сфер', 'вкладыва'] },
  { id: 'appeals_organizations', words: ['организац', 'куда обраща', 'адресат'] },
  { id: 'appeals_spheres', words: ['о чём пишут', 'тематик', 'темы обращен'] },
  { id: 'quick_summary', words: ['сводк', 'обстановка в целом', 'что по области'] },
  { id: 'top_regions', words: ['топ', 'районы', 'регион', 'где хуже'] },
  { id: 'crime_report_atyrau', words: ['преступн', 'криминоген', 'уголовн'] },
  { id: 'business_report', words: ['бизнес', 'предприниматель', 'инвест'] },
  { id: 'appeals_report', words: ['обращен', 'жалоб', 'отиныш', 'өтініш'] },
]

export function demoReplyForText(text: string): DemoReply | null {
  const q = text.toLowerCase()
  let best: { id: string; hits: number } | null = null
  for (const entry of KEYWORDS) {
    const hits = entry.words.filter((w) => q.includes(w)).length
    if (hits > 0 && (!best || hits > best.hits)) best = { id: entry.id, hits }
  }
  return best ? build(best.id) : null
}
